import { useSession, useTranslation, useNextgenRouter, useBreakpoint, Breakpoints } from '@nextgen-web-framework/all';
import { ConfigService } from '@nextgen-web-framework/core';
import {
  getAccountTypeData,
  VerticalTabNavigation,
  NavigationSource,
  MenuDropdown,
  useLoader,
} from '@commerce-webcomponents/ui';
import Layout from '../../../components/Layout/Layout';
import useLivePreview from '../../../live-preview/useLivePreview';
import withNextGenISR from '../../../utils/withNextGenISR';
import { CommonDataProps, PageCategory, CMSTransformerDataType } from '../../../types/common';
import { getContentPageData } from '../../../utils/getEssentials';
import { getInvalidateTime } from '../../../utils/common';
import styles from './amway-resources.module.scss';
import ContentTemplate from 'components/ContentTemplate/ContentTemplate';
import SEOComponent from 'components/SEOComponent/SEOComponent';
import PageContentErrorHandler from 'components/PageContent/PageContentErrorHandler';
import usePageContent from 'components/PageContent/usePageContent';
import withFeatureFlags from 'utils/withFeatureFlags';

interface ResourcePageProps extends CommonDataProps {
  params?: CMSTransformerDataType;
  notFoundPageData?: CMSTransformerDataType;
}

const AmwayResources = ({ data: initialData, params, notFoundPageData }: ResourcePageProps) => {
  const t = useTranslation();
  const router = useNextgenRouter();
  const { session } = useSession();
  const { showLoader, hideLoader } = useLoader();
  const viewPortType = useBreakpoint() as Breakpoints;
  const isDesktop = viewPortType === Breakpoints.LG || viewPortType === Breakpoints.XL;
  const enableFlag = ConfigService.getConfigs().NEXT_PUBLIC_ENABLE_AMWAY_RESTRICTION as string;
  const [parentCategory] = params.slug;
  const activeRoute = `/amway-resources/${parentCategory}`;

  const resourceContent = usePageContent({
    initialData: initialData,
    locale: params?.locale as string,
    uId: 'resource_and_download_page',
    queryUrl: `/amway-resources/${parentCategory}`,
    enableFlag,
    userType: session.type,
  });

  const [pageData, seoState] = useLivePreview(resourceContent.data, resourceContent.data?.seo_config);
  const header = pageData?.layout?.header;
  const footer = pageData?.layout?.footer;

  const goToRoute = (url: string) => {
    if (url !== activeRoute) {
      router.push(url);
    }
  };
  const transformNavigationLinks = (items: CMSTransformerDataType) =>
    items.map((item: CMSTransformerDataType) => ({
      label: item.title,
      url: item.url,
    }));

  if (resourceContent.hasError || resourceContent.notFound) {
    hideLoader();
  } else if (!Object.keys(pageData)?.length) {
    showLoader();
    return <></>;
  } else {
    hideLoader();
  }

  const userData = pageData?.navigations ? getAccountTypeData(pageData?.navigations, session.type) : null;
  const pageContent = (
    <ContentTemplate
      data={pageData}
      params={params}
      pagePath="amway-resources"
      componentKey="resource_card"
      uId="resource_card"
      leftNav={!!pageData?.navigations}
      sortByPublishedDate
    />
  );
  return (
    <PageContentErrorHandler content={resourceContent} notFoundData={notFoundPageData}>
      <Layout
        hasTopMobileHeader={true}
        headerProps={{
          headerElement: (
            <div className={styles['amway-resources-title']}>{t('amshop.resourceAndDownloadPageTitle')}</div>
          ),
          hasTabletScrolledHeaderBody: true,
          isStaticHeader: true,
          isBackNavigation: false,
          hasCart: true,
          hasSearchBar: true,
        }}
        hideLocationSelector={true}
        header={header}
        footer={footer}
      >
        <div className={styles['amway-resources-container']} data-testid="amway-resources-container-test-id">
          <SEOComponent {...seoState} />
          {userData?.navigations && isDesktop && (
            <VerticalTabNavigation
              source={NavigationSource.CONTENT}
              data={transformNavigationLinks(userData?.navigations)}
              activeRoute={activeRoute}
              goToRoute={goToRoute}
              content={pageContent}
              customClass={styles['left-tab-sticky']}
            />
          )}
          {userData?.navigations && !isDesktop && (
            <MenuDropdown
              menuData={transformNavigationLinks(userData?.navigations)}
              activeRoute={activeRoute}
              content={pageContent}
            />
          )}
        </div>
      </Layout>
    </PageContentErrorHandler>
  );
};

export const getStaticPaths = () => ({
  paths: [],
  fallback: 'blocking', //indicates the type of fallback
});

export const getStaticProps = withNextGenISR(async (context) => {
  const { data, notFoundData, showError } = await getContentPageData({
    params: context.params,
    uId: 'resource_and_download_page',
    url: `/amway-resources/${context.params?.slug && context.params?.slug[0]}`,
  });

  return {
    props: {
      data,
      showError: data ? showError : true,
      params: context.params,
      notFoundPageData: notFoundData,
    },
    revalidate: getInvalidateTime(PageCategory.DEFERRED),
  };
});

export default withFeatureFlags(AmwayResources);
